<template>
  <div
    class="modal-overlay"
    @mousedown.self="onBackdropPointerDown"
    @click.self="onBackdropClick($event, close)"
  >
    <div
      class="modal-content issue-report-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="issue-report-title"
      @click.stop
    >
      <div class="modal-header">
        <h3 id="issue-report-title">Report a Problem</h3>
        <button @click="close" class="close-btn" aria-label="Close">
          <X :size="18" />
        </button>
      </div>

      <div class="modal-body">
        <template v-if="!submitted">
          <div class="form-group">
            <label>What would you like to do?</label>
            <div class="report-type-picker" role="radiogroup" aria-label="Report type">
              <button
                v-for="t in reportTypes"
                :key="t.id"
                type="button"
                class="report-type-chip"
                :class="{ selected: reportType === t.id }"
                role="radio"
                :aria-checked="reportType === t.id"
                @click="reportType = t.id"
              >
                {{ t.label }}
              </button>
            </div>
          </div>

          <div class="form-group">
            <label for="issue-report-message">
              {{ messageLabel }} <span class="required">*</span>
            </label>
            <textarea
              id="issue-report-message"
              v-model="message"
              class="form-textarea"
              rows="6"
              :maxlength="maxLength"
              :placeholder="messagePlaceholder"
              autofocus
            ></textarea>
            <p class="field-hint char-count">{{ message.trim().length }} / {{ maxLength }}</p>
          </div>

          <p class="field-hint context-note">
            We automatically include the page you're on, your account, and browser type so we can
            track things down — no student data is attached. Please keep student names out of your
            message.
          </p>

          <div v-if="errorMessage" class="status-message error">{{ errorMessage }}</div>
        </template>

        <div v-else class="report-success">
          <CircleCheck :size="44" class="report-success-icon" aria-hidden="true" />
          <h4>Thanks — we got it!</h4>
          <p>
            Your {{ submittedTypeLabel }} was sent. We review every report, and it genuinely shapes
            what gets fixed next.
          </p>
        </div>
      </div>

      <div class="modal-footer">
        <template v-if="!submitted">
          <button @click="close" class="secondary-btn" :disabled="submitting">Cancel</button>
          <button @click="submit" class="primary-btn" :disabled="!canSubmit">
            <span v-if="submitting">Sending…</span>
            <span v-else>Send</span>
          </button>
        </template>
        <button v-else @click="close" class="primary-btn">Done</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { X, CircleCheck } from 'lucide-vue-next'
import { useBackdropDismiss } from '@/composables/useBackdropDismiss'
import {
  useIssueReports,
  ISSUE_REPORT_TYPES,
  ISSUE_REPORT_MAX_LENGTH
} from '@/composables/useIssueReports'

const emit = defineEmits(['close'])

const { onBackdropPointerDown, onBackdropClick } = useBackdropDismiss()
const { submitIssueReport } = useIssueReports()

const reportTypes = ISSUE_REPORT_TYPES
const maxLength = ISSUE_REPORT_MAX_LENGTH

const reportType = ref('problem')
const message = ref('')
const submitting = ref(false)
const submitted = ref(false)
const errorMessage = ref('')

const messageLabel = computed(() =>
  reportType.value === 'question' ? 'Your question' : 'Describe what happened'
)

const messagePlaceholder = computed(() => {
  switch (reportType.value) {
    case 'question':
      return 'What would you like to know? The more detail, the better the answer.'
    case 'suggestion':
      return 'What would make the app work better for you?'
    default:
      return 'What were you trying to do, and what went wrong? Include any error message you saw.'
  }
})

const submittedTypeLabel = computed(
  () => reportTypes.find((t) => t.id === reportType.value)?.label.toLowerCase() || 'report'
)

const canSubmit = computed(() => message.value.trim().length > 0 && !submitting.value)

async function submit() {
  if (!canSubmit.value) return
  submitting.value = true
  errorMessage.value = ''
  try {
    await submitIssueReport({ type: reportType.value, message: message.value })
    submitted.value = true
  } catch (error) {
    console.error('Issue report failed:', error)
    errorMessage.value =
      'Something went wrong sending your report. Please try again — or email us directly if it keeps happening.'
  } finally {
    submitting.value = false
  }
}

function close() {
  if (submitting.value) return
  emit('close')
}

function onKeydown(event) {
  if (event.key === 'Escape') close()
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.issue-report-dialog {
  max-width: 520px;
}

.report-type-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.report-type-chip {
  padding: 8px 14px;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 999px;
  background: var(--bg-secondary, #fff);
  color: var(--text-secondary, #374151);
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.report-type-chip:hover {
  background: var(--bg-tertiary, #f1f3f5);
}

.report-type-chip.selected {
  border-color: var(--primary-color, #1b75d2);
  background: var(--primary-bg-subtle, rgba(27, 117, 210, 0.1));
  color: var(--primary-color, #1b75d2);
  font-weight: 600;
}

.report-type-chip:focus-visible {
  outline: 2px solid var(--primary-color, #1b75d2);
  outline-offset: 2px;
}

.required {
  color: var(--error-color, #dc3545);
}

.char-count {
  text-align: right;
}

.context-note {
  margin-top: 4px;
  padding: 10px 12px;
  background: var(--bg-tertiary, #f8f9fa);
  border-radius: 8px;
  line-height: 1.45;
}

.report-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 8px;
  gap: 8px;
}

.report-success-icon {
  color: var(--success-color, #28a745);
}

.report-success h4 {
  margin: 0;
  color: var(--text-primary, #1f2937);
  font-size: 1.1rem;
}

.report-success p {
  margin: 0;
  color: var(--text-muted, #6c757d);
  font-size: 0.92rem;
  line-height: 1.5;
  max-width: 380px;
}

.status-message.error {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(220, 53, 69, 0.1);
  color: var(--error-color, #dc3545);
  font-size: 0.9rem;
}
</style>
